import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, type Follow } from "@/lib/api";
import { handleUpgradeError } from "@/components/UpgradeDialog";
import { analytics } from "@/lib/analytics";

export type FollowTarget = {
  id: number;
  /** Human-readable label for the toast ("NANOBIOTIX", "e.lafage", …). */
  label: string;
};

/**
 * useToggleFollow centralises the follow/unfollow mutation that used to be
 * copy-pasted across ForumsPage / UsersPage / OnboardingPage, and adds an
 * OPTIMISTIC update on the ["follows"] cache : the star fills instantly
 * instead of waiting ~300-500 ms for invalidate+refetch. Rolled back on error.
 */
export function useToggleFollow(targetType: Follow["target_type"]) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (target: FollowTarget) => {
      const follows = qc.getQueryData<Follow[]>(["follows"]) ?? [];
      const existing = follows.find(
        (f) => f.target_type === targetType && Number(f.target_id) === target.id
      );
      if (existing && Number(existing.id) > 0) {
        await api.deleteFollow(Number(existing.id));
        return { action: "removed" as const, target };
      }
      await api.createFollow({ target_type: targetType, target_id: target.id });
      return { action: "added" as const, target };
    },
    onMutate: async (target) => {
      await qc.cancelQueries({ queryKey: ["follows"] });
      const previous = qc.getQueryData<Follow[]>(["follows"]);
      qc.setQueryData<Follow[]>(["follows"], (old = []) => {
        const existing = old.find(
          (f) => f.target_type === targetType && Number(f.target_id) === target.id
        );
        if (existing) {
          return old.filter((f) => f !== existing);
        }
        // Temporary negative id until the server-assigned row is refetched.
        return [
          {
            id: -Date.now(),
            target_type: targetType,
            target_id: target.id,
            created_at: new Date().toISOString(),
          },
          ...old,
        ];
      });
      return { previous };
    },
    onSuccess: ({ action, target }) => {
      analytics.toggleFollow(targetType, action === "added" ? "follow" : "unfollow", target.id);
      toast.success(
        action === "added" ? `« ${target.label} » suivi` : `« ${target.label} » retiré des suivis`
      );
    },
    onError: (e, _target, ctx) => {
      if (ctx?.previous) qc.setQueryData(["follows"], ctx.previous);
      if (handleUpgradeError(e)) return;
      toast.error(`Erreur : ${e instanceof Error ? e.message : "?"}`);
    },
    onSettled: () => {
      // Re-sync with the server (real follow id, free-tier count, …).
      qc.invalidateQueries({ queryKey: ["follows"] });
    },
  });
}
